import type { Habit, HabitLog } from "./db";
import { addDays, APP_TIMEZONE, localDateKey, weekStart } from "./time";

// Habit heatmap (HB-4): for each day, how many daily habits were due and how
// many were met. Weekly habits have no daily target, so they are left out.

export type HeatDay = { date: string; due: number; done: number; future: boolean };

/** `weeks` columns ending with the current ISO week; each column is Monday→Sunday. */
export function habitHeatmap(input: {
	habits: Habit[];
	logs: HabitLog[];
	minimumDays: ReadonlySet<string>;
	today: string;
	weeks?: number;
	timeZone?: string;
}): HeatDay[][] {
	const tz = input.timeZone ?? APP_TIMEZONE;
	const weeks = input.weeks ?? 12;
	const daily = input.habits.filter((h) => !h.deleted_at && !h.archived && h.cadence === "daily");
	const count = new Map<string, number>();
	for (const l of input.logs) if (!l.deleted_at) count.set(`${l.habit_id}:${l.date}`, l.count);
	const starts = new Map(daily.map((h) => [h.id, localDateKey(new Date(h.created_at), tz)]));

	const first = addDays(weekStart(input.today), -7 * (weeks - 1));
	return Array.from({ length: weeks }, (_, w) =>
		Array.from({ length: 7 }, (_, d) => {
			const date = addDays(first, w * 7 + d);
			if (date > input.today) return { date, due: 0, done: 0, future: true };
			let due = 0;
			let done = 0;
			for (const h of daily) {
				if (date < starts.get(h.id)!) continue;
				if (input.minimumDays.has(date) && !h.is_minimum) continue;
				due++;
				if ((count.get(`${h.id}:${date}`) ?? 0) >= h.target_per_period) done++;
			}
			return { date, due, done, future: false };
		}),
	);
}
